// Encrypted offline store. Form drafts and other field captures are
// held in IndexedDB so a worker can keep capturing with no signal; everything
// is encrypted at rest and synced later.
//
// Encryption: AES-GCM with a per-(device, worker) key derived via HKDF from a
// 32-byte deviceSecret (generated on first run, cached in IndexedDB). Mixing
// the workerId into the HKDF `info` means a different worker signing in on the
// same device derives a different key and cannot read the first worker's
// records — cryptographic isolation on top of the (device + worker) pin. A
// fresh 12-byte IV per write; the stored value is { iv, ct }.
//
// The deviceSecret is deliberately persistent (not session-scoped): a field
// worker must be able to read their drafts across the offline window without a
// live token. Destroying the secret (wipe) is what makes every
// ciphertext unrecoverable. Records are pinned with (deviceId, workerId,
// expiresAt); the ~7-day TTL + pin sweep harden in .

// The WRITE half of this store — putOffline and the AES-GCM wrap — lives in
// services/offlineWrite.ts, which imports these internals. It is split off so
// the encrypt path rides the lazy chunk of whatever captures (today: the proof
// upload), not main.js: no eager code writes the queue, so the write path was
// dead code in main and would otherwise be pulled back in by the first lazy
// caller. Everything exported with an `_` prefix below is for that file only.

const DB_NAME = 'fexo';
const DB_VERSION = 1;
const STORE_SECRETS = 'secrets';
export const _STORE_RECORDS = 'sync_queue';

// Offline-allowed entity kinds (offline-scope discipline). This union is the
// enforcement point: online-only operations (server-side scoring, cross-record
// lookup, approvals, and reports) are intentionally absent — they cannot be
// stored offline because there is no kind for them.
// `proof` is the typed proof-of-work capture (trip-domain §5.3, DATA-015):
// trips only, append-only, and the ONE kind whose record carries bytes — the
// artifact rides the encrypted store until the drain has minted a signed URL
// and PUT it straight to the bucket; only then does the `proof` mutation (ref,
// no bytes) go to /api/sync. Size posture: the payload is JSON, so the bytes
// are base64 (×4/3) before AES-GCM (+28 B); a 1440×2560 phone JPEG of ~0.8 MB
// is ~1.1 MB at rest, and the 25 MiB server cap bounds one record at ~34 MB.
export type OfflineKind = 'draft' | 'attendance' | 'note' | 'observation' | 'proof';

export interface OfflineEntry<T> {
  id: string;
  payload: T;
}

export type _Wrapped = { iv: ArrayBuffer; ct: ArrayBuffer };

// PII lives encrypted in `blob`; the rest is opaque ids + timestamps (no PII)
// kept in the clear so the store can pin, expire, list, and sync without
// decrypting. `module`, `clientMutationId`, and `updatedAt` are the sync
// metadata: the owning module (the receiver's authorization axis), a fresh-per-
// write idempotency key, and the last-edit time the server uses as the
// last-write-wins clock.
export interface _StoredRecord {
  id: string; // `${kind}:${rawId}`
  kind: OfflineKind;
  deviceId: string;
  workerId: string;
  module: string;
  clientMutationId: string;
  updatedAt: number;
  expiresAt: number;
  blob: _Wrapped;
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function _openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE_SECRETS)) db.createObjectStore(STORE_SECRETS);
      if (!db.objectStoreNames.contains(_STORE_RECORDS)) db.createObjectStore(_STORE_RECORDS, { keyPath: 'id' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  return dbPromise;
}

async function getOrCreateDeviceSecret(): Promise<ArrayBuffer> {
  const db = await _openDB();
  const store = db.transaction(STORE_SECRETS, 'readwrite').objectStore(STORE_SECRETS);
  const existing = await _req(store.get('device_secret'));
  if (existing instanceof ArrayBuffer) return existing;
  const fresh = crypto.getRandomValues(new Uint8Array(32)).buffer;
  await _req(store.put(fresh, 'device_secret'));
  return fresh;
}

export async function _deriveKey(workerId: string): Promise<CryptoKey> {
  const secret = await getOrCreateDeviceSecret();
  const ikm = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('fexo:pii:v1'),
      info: new TextEncoder().encode(`worker:${workerId}`),
    },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function unwrap(w: _Wrapped, workerId: string): Promise<string> {
  const key = await _deriveKey(workerId);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(w.iv) }, key, w.ct);
  return new TextDecoder().decode(pt);
}

export function _recordKey(kind: OfflineKind, rawId: string): string {
  return `${kind}:${rawId}`;
}

function isReadable(rec: _StoredRecord, workerId: string, did: string): boolean {
  return rec.workerId === workerId && rec.deviceId === did && rec.expiresAt > Date.now();
}

// getOffline returns the decrypted payload, or null when absent, expired, or
// pinned to a different device/worker.
export async function getOffline<T>(
  kind: OfflineKind,
  rawId: string,
  workerId: string
): Promise<T | null> {
  const db = await _openDB();
  const rec = (await _req(
    db.transaction(_STORE_RECORDS, 'readonly').objectStore(_STORE_RECORDS).get(_recordKey(kind, rawId))
  )) as _StoredRecord | undefined;
  if (!rec || !isReadable(rec, workerId, await _deviceId())) return null;
  return JSON.parse(await unwrap(rec.blob, workerId)) as T;
}

// listOffline returns every readable record of a kind for this device + worker,
// decrypted, with the kind prefix stripped from the id.
export async function listOffline<T>(kind: OfflineKind, workerId: string): Promise<Array<OfflineEntry<T>>> {
  const db = await _openDB();
  const all = (await _req(
    db.transaction(_STORE_RECORDS, 'readonly').objectStore(_STORE_RECORDS).getAll()
  )) as _StoredRecord[];
  const did = await _deviceId();
  const out: Array<OfflineEntry<T>> = [];
  for (const rec of all) {
    if (rec.kind !== kind || !isReadable(rec, workerId, did)) continue;
    out.push({ id: rec.id.slice(kind.length + 1), payload: JSON.parse(await unwrap(rec.blob, workerId)) as T });
  }
  return out;
}

// removeOffline drops a record (e.g. once the sync layer has uploaded it).
export async function removeOffline(kind: OfflineKind, rawId: string): Promise<void> {
  const db = await _openDB();
  await _req(db.transaction(_STORE_RECORDS, 'readwrite').objectStore(_STORE_RECORDS).delete(_recordKey(kind, rawId)));
}

// PendingMutation is one queued record ready to sync — the decrypted payload
// plus the metadata the /api/sync receiver needs to build a mutation.
export interface PendingMutation<T = unknown> {
  kind: OfflineKind;
  rawId: string;
  clientMutationId: string;
  module: string;
  updatedAt: number;
  payload: T;
}

// listPending returns every readable record for this device + worker across all
// kinds, decrypted, with its sync metadata — the source the background-sync
// drain (services/sync.ts) builds its /api/sync batch from. Like listOffline it
// honours the (device, worker) pin and the TTL, so an expired or foreign record
// is never synced.
export async function listPending(workerId: string): Promise<PendingMutation[]> {
  const db = await _openDB();
  const all = (await _req(
    db.transaction(_STORE_RECORDS, 'readonly').objectStore(_STORE_RECORDS).getAll()
  )) as _StoredRecord[];
  const did = await _deviceId();
  const out: PendingMutation[] = [];
  for (const rec of all) {
    if (!isReadable(rec, workerId, did)) continue;
    out.push({
      kind: rec.kind,
      rawId: rec.id.slice(rec.kind.length + 1),
      clientMutationId: rec.clientMutationId,
      module: rec.module,
      updatedAt: rec.updatedAt,
      payload: JSON.parse(await unwrap(rec.blob, workerId)) as unknown,
    });
  }
  return out;
}

// removeIfSynced drops a record only if its clientMutationId still matches the
// one that was synced. A re-edit during the sync round-trip regenerates the id
// (putOffline mints a fresh one), so a compare-and-delete preserves that newer
// pending edit instead of clobbering it — the difference between "this mutation
// landed, clear it" and "clear whatever is here now".
export async function removeIfSynced(
  kind: OfflineKind,
  rawId: string,
  clientMutationId: string
): Promise<void> {
  const db = await _openDB();
  const store = db.transaction(_STORE_RECORDS, 'readwrite').objectStore(_STORE_RECORDS);
  const rec = (await _req(store.get(_recordKey(kind, rawId)))) as _StoredRecord | undefined;
  if (rec && rec.clientMutationId === clientMutationId) {
    await _req(store.delete(_recordKey(kind, rawId)));
  }
}

// sweepExpired deletes every record past its TTL, actually purging the
// encrypted PII (a read only hides it). Metadata-only — no key needed — so it
// purges expired records belonging to any worker/device on this device, which
// is the right DPA behavior: the ~7-day offline retention is enforced
// regardless of who is signed in. Returns the count purged.
export async function sweepExpired(): Promise<number> {
  const db = await _openDB();
  const store = db.transaction(_STORE_RECORDS, 'readwrite').objectStore(_STORE_RECORDS);
  const all = (await _req(store.getAll())) as _StoredRecord[];
  const now = Date.now();
  let purged = 0;
  for (const rec of all) {
    if (rec.expiresAt <= now) {
      await _req(store.delete(rec.id));
      purged += 1;
    }
  }
  return purged;
}

// Hourly cadence covers a long-lived offline PWA session where records expire
// mid-use; the immediate sweep handles the common boot case.
const SWEEP_INTERVAL_MS = 60 * 60 * 1000;

// startExpirySweep sweeps now, then hourly. Best-effort: a failed sweep must
// never wedge the app. Returns a stop function (the app runs for its lifetime;
// the handle is there for tests and teardown).
export function startExpirySweep(): () => void {
  void sweepExpired().catch(() => undefined);
  const timer = setInterval(() => void sweepExpired().catch(() => undefined), SWEEP_INTERVAL_MS);
  return () => clearInterval(timer);
}

// wipeOfflineData destroys the entire offline database — every encrypted record
// and the device secret that derives their keys (compromised-device
// wipe). Dropping the secret is what makes the wipe irreversible: even if a
// ciphertext were recovered, its key is gone. The service worker deletes the
// same database independently on its `compromised` message; this app-context
// delete is the belt-and-suspenders path when there is no SW controller.
// Best-effort and non-blocking — success, error, and blocked all resolve, since
// the compromise flow must never hang on the wipe.
export async function wipeOfflineData(): Promise<void> {
  if (dbPromise) {
    try {
      (await dbPromise).close();
    } catch {
      // already closing / closed — we're deleting it regardless
    }
    dbPromise = null;
  }
  await new Promise<void>((resolve) => {
    const del = indexedDB.deleteDatabase(DB_NAME);
    del.onsuccess = () => resolve();
    del.onerror = () => resolve();
    del.onblocked = () => resolve();
  });
}

export async function _deviceId(): Promise<string> {
  const db = await _openDB();
  const store = db.transaction(STORE_SECRETS, 'readwrite').objectStore(STORE_SECRETS);
  const existing = (await _req(store.get('device_id'))) as string | undefined;
  if (typeof existing === 'string') return existing;
  const fresh = crypto.randomUUID();
  await _req(store.put(fresh, 'device_id'));
  return fresh;
}

export function _req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
